import Vue from 'vue';

// Uploads are sent one at a time, so a weak connection is not shared between parallel uploads.
// Failed uploads keep their file in memory so the user can retry without taking a new picture.

export type UploadState = 'uploading' | 'failed';

export interface UploadStatus {
  state: UploadState;
  progress: number;
  error: string;
  file: Blob;
  previewUrl: string;
}

const RETRY_DELAYS = [2000, 5000, 10000];

// Global overview of uploads that are not saved yet (key -> state), used to warn before leaving
export const uploadState = Vue.observable({ items: {} as { [key: string]: UploadState } });

export function setUploadState(key: string, state: UploadState | null) {
  if (state) {
    Vue.set(uploadState.items, key, state);
  } else {
    Vue.delete(uploadState.items, key);
  }
}

export function countUploads(state: UploadState): number {
  return Object.values(uploadState.items).filter(s => s === state).length;
}

export function hasUnsavedUploads(): boolean {
  return Object.keys(uploadState.items).length > 0;
}

export function unsavedUploadsWarning(): string {
  const uploading = countUploads('uploading');
  const failed = countUploads('failed');
  const parts: string[] = [];
  if (uploading) {
    parts.push(uploading + ' foto(\'s) worden nog geüpload');
  }
  if (failed) {
    parts.push(failed + ' foto(\'s) zijn NIET opgeslagen');
  }
  return parts.join(' en ') + '. Toch verlaten?';
}

window.addEventListener('beforeunload', event => {
  if (hasUnsavedUploads()) {
    event.preventDefault();
    event.returnValue = '';
  }
});

let queue: Promise<unknown> = Promise.resolve();

export function enqueueUpload<T>(task: () => Promise<T>): Promise<T> {
  const result = queue.then(task, task);
  queue = result.catch(() => undefined);
  return result;
}

function isRetryable(error): boolean {
  if (!error || !error.response) {
    return true; // network error or timeout
  }
  return [502, 503, 504].includes(error.response.status);
}

export async function withRetry<T>(task: () => Promise<T>): Promise<T> {
  for (let attempt = 0; ; attempt++) {
    try {
      return await task();
    } catch (error) {
      if (attempt >= RETRY_DELAYS.length || !isRetryable(error)) {
        throw error;
      }
      await new Promise(resolve => setTimeout(resolve, RETRY_DELAYS[attempt]));
    }
  }
}
